import React from 'react';

import { SidePage, Section, Actions, Action } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import { useScreenGo } from '@gogame/screens';

import AffiliateNote from 'scenes/Campaigns/components/AffiliateNote';
import { ParentCampaignActions } from 'scenes/Campaigns/ParentCampaigns/components/ParentCampaignActions';
import {
  useLoadParentCampaignDetails,
  useCopyCampaignHybridLink,
} from 'scenes/Campaigns/ParentCampaigns/data';
import { useCopyLink } from 'scenes/Campaigns/hooks';
import AffiliateDetails from 'scenes/Affiliates/components/AffiliateDetails';
import PromoCodeDetails from 'scenes/PromoCodes/components/PromoCodeDetails';

import {
  ParentCampaignFormEdit,
  ParentCampaignFormDuplicateWithChaining,
} from 'scenes/Campaigns/ParentCampaigns/components/ParentCampaignForm';
import { FraudProtection } from 'scenes/Campaigns/components/Details/FraudProtection';
import { campaignDetailsLink } from 'scenes/Campaigns/GGCampaigns/navigation';

import GGCampaigns from './GGCampaigns';
import General from './General';
import Metadata from './Metadata';

export const ParentCampaignDetails = ({ id, onClose, onExited, open }) => {
  const [childComponent, openChildComponent] = useChildComponent();
  const { data, isLoading, isRefetching } = useLoadParentCampaignDetails({
    id,
  });

  const handleCopyCampaignHybridLink = useCopyCampaignHybridLink();

  const copyLink = useCopyLink();
  const go = useScreenGo();

  const handleEdit = ({ id, name }) => {
    openChildComponent(ParentCampaignFormEdit, {
      name: name,
      id: id,
    });
  };

  const handleDuplicate = ({ id }) => {
    openChildComponent(ParentCampaignFormDuplicateWithChaining, {
      id: id,
      onSubmit: ([campaign]) => {
        if (campaign?.parentCampaign?.id) {
          go(`../${campaign.parentCampaign.id}`);
          return;
        }

        go(
          `../../${campaignDetailsLink.absolutePath.replace(':id', campaign.id)}`
        );
      },
    });
  };

  const handleOpenAffiliate = (_e, { id }) => {
    openChildComponent(AffiliateDetails, {
      id,
    });
  };

  const handleOpenPromoCodeDetails = (id) => {
    openChildComponent(PromoCodeDetails, {
      id,
    });
  };

  return (
    <SidePage
      backdrop={false}
      onClose={onClose}
      onExited={onExited}
      loading={isLoading}
      fetching={isRefetching}
      open={open}
      size={'lg'}
    >
      <SidePage.Title
        title={'Parent Campaign'}
        entityId={id}
        subTitle={data?.name}
      />
      <SidePage.NewBody wrapper={true} dense={true}>
        <SidePage.NewContent withSidebar={true}>
          <Section title={'General'}>
            <General
              data={data}
              onOpenAffiliate={handleOpenAffiliate}
              onOpenPromoCodeDetails={handleOpenPromoCodeDetails}
              onCopyLink={copyLink}
            />
          </Section>
          {data?.campaigns?.length && (
            <Section
              title={`GG Campaigns (${data.campaigns.length})`}
              isCollapsable={true}
              initialOpen={true}
              action={
                <Actions>
                  {data.hybridLink ? (
                    <Action
                      button
                      onClick={() =>
                        handleCopyCampaignHybridLink({
                          hybridLink: data.hybridLink,
                        })
                      }
                      label={'Copy hybrid link'}
                    />
                  ) : null}
                </Actions>
              }
            >
              <GGCampaigns campaigns={data.campaigns} />
            </Section>
          )}
          {data?.fraudProtections?.length ? (
            <Section
              title={`Fraud Protection (${data.fraudProtections.length})`}
              isCollapsable={true}
              initialOpen={false}
            >
              <FraudProtection fraudProtections={data.fraudProtections} />
            </Section>
          ) : null}
          {data && (
            <Section>
              <AffiliateNote />
            </Section>
          )}
        </SidePage.NewContent>
        <SidePage.Sidebar
          actions={
            <ParentCampaignActions
              limit={3}
              data={data}
              onEdit={handleEdit}
              onDuplicate={handleDuplicate}
            />
          }
        >
          <Metadata data={data} />
        </SidePage.Sidebar>
      </SidePage.NewBody>
      <SidePage.Actions>
        <SidePage.CloseButton />
      </SidePage.Actions>
      {childComponent}
    </SidePage>
  );
};

export default ParentCampaignDetails;
